import { useCallback, useMemo, useRef } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import {
  forceStartRecording,
  runStatsigTriggeredSessionReplay,
  stopRecording,
} from '@statsig/session-replay';

import { useTrackEvent } from './useTrackEvent';

interface SessionState {
  isRecording: boolean;
  sessionKey: string | null;
  startTime: number | null;
}

export function useSessionReplayCapture() {
  const { client } = useStatsigClient();
  const { trackEvent } = useTrackEvent();
  const sessionStateRef = useRef<SessionState>({
    isRecording: false,
    sessionKey: null,
    startTime: null,
  });

  const startCapture = useCallback(
    (sessionKey: string, metadata?: Record<string, unknown>) => {
      const state = sessionStateRef.current;

      // Check if a different session is already recording
      if (state.isRecording && state.sessionKey !== sessionKey) {
        // Log that this session was requested but blocked
        trackEvent('session_replay_requested', {
          metadata: {
            sessionKey,
            blockedBy: state.sessionKey || '',
            ...metadata,
          },
        });
        return false;
      }

      // If the same session is already recording, just return true
      if (state.isRecording && state.sessionKey === sessionKey) {
        return true;
      }

      // Start new recording
      try {
        // Update state first
        sessionStateRef.current = {
          isRecording: true,
          sessionKey,
          startTime: Date.now(),
        };

        // Force start the recording since autoStartRecording is false
        forceStartRecording();

        // Log the start event
        trackEvent('session_replay_started', {
          metadata: {
            sessionKey,
            ...metadata,
          },
        });

        return true;
      } catch (error) {
        console.error('Failed to start session replay:', error);
        // Reset state on error
        sessionStateRef.current = {
          isRecording: false,
          sessionKey: null,
          startTime: null,
        };
        return false;
      }
    },
    [trackEvent],
  );

  const stopCapture = useCallback(
    (sessionKey: string, metadata?: Record<string, unknown>) => {
      const state = sessionStateRef.current;

      // Always log the stop request
      trackEvent('session_replay_stop_requested', {
        metadata: {
          sessionKey,
          currentSession: state.sessionKey || '',
          isRecording: String(state.isRecording),
          ...metadata,
        },
      });

      // Only stop if there's an active recording AND it's our session
      if (!state.isRecording || state.sessionKey !== sessionKey) {
        return false;
      }

      try {
        // Calculate duration
        const duration = state.startTime ? Date.now() - state.startTime : 0;

        // Log successful stop
        trackEvent('session_replay_stopped', {
          value: duration,
          metadata: {
            sessionKey,
            duration: String(duration),
            ...metadata,
          },
        });

        // Stop the recording and trigger upload
        stopRecording();
        runStatsigTriggeredSessionReplay(client);

        // Reset state
        sessionStateRef.current = {
          isRecording: false,
          sessionKey: null,
          startTime: null,
        };

        return true;
      } catch (error) {
        console.error('Failed to stop session replay:', error);
        return false;
      }
    },
    [trackEvent, client],
  );

  const checkIsRecording = useCallback((sessionKey?: string) => {
    const state = sessionStateRef.current;
    if (sessionKey) {
      return state.isRecording && state.sessionKey === sessionKey;
    }
    return state.isRecording;
  }, []);

  const getCurrentSessionKey = useCallback(() => {
    return sessionStateRef.current.sessionKey;
  }, []);

  const getSessionDuration = useCallback(() => {
    const state = sessionStateRef.current;
    if (!state.isRecording || !state.startTime) {
      return 0;
    }
    return Date.now() - state.startTime;
  }, []);

  return useMemo(
    () => ({
      startCapture,
      stopCapture,
      checkIsRecording,
      getCurrentSessionKey,
      getSessionDuration,
    }),
    [startCapture, stopCapture, checkIsRecording, getCurrentSessionKey, getSessionDuration],
  );
}
